import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const ReadBook = () => {
  const pdfUrl = "/assets/books/book.pdf"; // put your PDF here (public/assets/books/book.pdf)
  const [pages, setPages] = useState([]);
  const [numPages, setNumPages] = useState(0);
  const pdfRef = React.useRef(null);
  const [currentPage, setCurrentPage] = useState(0);

  // count of active page-render jobs — used to show a small loading spinner while lazy-rendering
  const [renderingCount, setRenderingCount] = useState(0);

  const [animDir, setAnimDir] = useState("next");
  const [flipImage, setFlipImage] = useState(null);
  const [flipActive, setFlipActive] = useState(false);
  const [flipRotate, setFlipRotate] = useState(false);
  const [isSinglePage, setIsSinglePage] = useState(false);

  // render initial pages; renderPage is stable enough here and we intentionally
  // do not add it to deps to avoid re-running this effect as pages are cached.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onResize = () => setIsSinglePage(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadPdf = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        pdfRef.current = pdf;
        const total = pdf.numPages;
        if (!mounted) return;
        setNumPages(total);
        setPages(Array(total).fill(null));
        // lazily render first spread (inline to avoid dependence on renderPage here)
        const inlineRender = async (idx) => {
          if (idx < 0 || idx >= pdf.numPages) return;
          setRenderingCount((c) => c + 1);
          try {
            const page = await pdf.getPage(idx + 1);
            const viewport = page.getViewport({ scale: 1.5 });
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            await page.render({ canvasContext: ctx, viewport }).promise;
            const dataUrl = canvas.toDataURL("image/png");
            setPages((prev) => {
              const copy = prev.slice();
              copy[idx] = dataUrl;
              return copy;
            });
          } catch (err) {
            console.error("inlineRender error", err);
          } finally {
            setRenderingCount((c) => Math.max(0, c - 1));
          }
        };

        await inlineRender(0);
        if (total > 1) await inlineRender(1);
        // also render back-cover if it exists (last page) so cover spread can show it
        if (total > 2) await inlineRender(total - 1);
      } catch (err) {
        console.error("Failed to load PDF", err);
      }
    };

    loadPdf();
    return () => {
      mounted = false;
    };
  }, []);

  // render a specific page into a data URL and cache it
  const renderPage = async (idx) => {
    if (!pdfRef.current) return null;
    if (idx < 0 || idx >= pdfRef.current.numPages) return null;
    // return cached
    if (pages[idx]) return pages[idx];
    setRenderingCount((c) => c + 1);
    try {
      const page = await pdfRef.current.getPage(idx + 1);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: ctx, viewport }).promise;
      const dataUrl = canvas.toDataURL("image/png");
      setPages((prev) => {
        const copy = prev.slice();
        copy[idx] = dataUrl;
        return copy;
      });
      return dataUrl;
    } catch (err) {
      console.error("renderPage error", err);
      return null;
    } finally {
      setRenderingCount((c) => Math.max(0, c - 1));
    }
  };

  // duration for the page-flip animation (ms) — increased for a slower, smoother flip
  const ANIM_DURATION = 900; // ms

  const startFlip = async (targetIndex, direction) => {
    // Immediately change pages without animation to avoid jank or delays.
    if (!pdfRef.current) return;
    if (targetIndex < 0 || targetIndex >= pdfRef.current.numPages) return;

    setAnimDir(direction);

    // Ensure the target and nearby pages are rendered (lazy render)
    await renderPage(targetIndex);
    renderPage(Math.max(0, targetIndex + 1));
    renderPage(Math.max(0, targetIndex - 1));

    // Special-case: if coming from single cover (0) to spread (1), keep existing instant jump behavior
    if (currentPage === 0 && targetIndex === 1) {
      setCurrentPage(1);
      return;
    }

    // Directly set the page without triggering the flip overlay/rotation
    setCurrentPage(targetIndex);
    // clear any flip state just in case
    setFlipActive(false);
    setFlipRotate(false);
    setFlipImage(null);
  };

  const onPrev = () => {
    // compute current spread start (even index)
    const spreadStart =
      currentPage === 0
        ? 0
        : currentPage % 2 === 0
        ? currentPage
        : Math.max(0, currentPage - 1);
    // move back one spread (two pages) unless we're on the cover spread
    const target = Math.max(0, spreadStart - 2);
    startFlip(target, "prev");
  };

  const onNext = () => {
    const total = pdfRef.current ? pdfRef.current.numPages : pages.length;
    // special-case: from single cover (0) go to page 1
    if (currentPage === 0) {
      startFlip(Math.min(total - 1, 1), "next");
      return;
    }
    // compute current spread start (even index)
    const spreadStart =
      currentPage % 2 === 0 ? currentPage : Math.max(0, currentPage - 1);
    // move forward one spread (two pages)
    const target = Math.min(total - 1, spreadStart + 2);
    startFlip(target, "next");
  };

  const totalForProgress = numPages || pages.length;
  const progress = totalForProgress
    ? Math.round(((currentPage + 1) / totalForProgress) * 100)
    : 0;

  return (
    <div className="read-book-page" style={{ padding: "20px" }}>
      <div style={{ marginBottom: "12px" }}>
        <Link to="/book-details" className="btn btn-outline btn-back-home">
          Back to Details
        </Link>
      </div>

      <div style={{ position: "relative", minHeight: "60vh" }}>
        {/* Left arrow */}
        <button
          onClick={onPrev}
          aria-label="Previous"
          className="btn btn-main"
          style={{
            position: "absolute",
            left: 10,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 20,
          }}
        >
          ◀
        </button>

        {/* Flipbook (two-page spread with improved animation) */}
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {pages.length > 0 ? (
            (() => {
              const singleCover = currentPage === 0;
              const spreadStart = singleCover
                ? 0
                : currentPage % 2 === 0
                ? currentPage
                : Math.max(0, currentPage - 1);
              const leftSrc = pages[spreadStart] || null;
              const rightSrc = pages[spreadStart + 1] || null;
              const isCover = spreadStart === 0; // special cover spread
              const backIndex =
                numPages && numPages > 0
                  ? numPages - 1
                  : Math.max(0, pages.length - 1);
              const backSrc = pages[backIndex] || null;
              return (
                <div
                  className="spread"
                  style={{
                    position: "relative",
                    display: "flex",
                    gap: 20,
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "min(80vh, 900px)",
                  }}
                >
                  {/* left page */}
                  <div
                    className="book-page left"
                    style={{
                      flex: 1,
                      background: isCover
                        ? "linear-gradient(90deg,#f3f3f3,#ededed)"
                        : "#fff",
                      boxShadow: isCover
                        ? "0 30px 80px rgba(0,0,0,0.22)"
                        : "0 18px 50px rgba(0,0,0,0.18)",
                      borderRadius: 6,
                      overflow: "hidden",
                      minHeight: 400,
                      display: isSinglePage || singleCover ? "none" : "block",
                      position: "relative",
                      transform: isCover ? "translateX(-6px)" : "none",
                    }}
                  >
                    {isCover ? (
                      backSrc ? (
                        <img
                          src={backSrc}
                          alt="back-cover"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#777",
                            fontSize: 18,
                          }}
                        >
                          {/* Decorative back-of-cover fallback */}
                          <div style={{ textAlign: "center" }}>
                            <div style={{ fontSize: 24, fontWeight: 700 }}>
                              Cover
                            </div>
                            <div
                              style={{
                                marginTop: 8,
                                fontSize: 12,
                                opacity: 0.8,
                              }}
                            >
                              Back
                            </div>
                          </div>
                        </div>
                      )
                    ) : (
                      leftSrc && (
                        <img
                          src={leftSrc}
                          alt="left page"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      )
                    )}
                    {/* subtle inner shadow */}
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        boxShadow: "inset -40px 0 60px rgba(0,0,0,0.06)",
                      }}
                    />
                  </div>

                  {/* right page */}
                  <div
                    className="book-page right"
                    style={{
                      flex: 1,
                      background: "#fff",
                      boxShadow: isCover
                        ? "0 36px 90px rgba(0,0,0,0.28)"
                        : "0 18px 50px rgba(0,0,0,0.18)",
                      borderRadius: isCover ? 8 : 6,
                      overflow: "hidden",
                      minHeight: 400,
                      position: "relative",
                      transform: isCover
                        ? "translateX(6px) skewY(-1deg)"
                        : "none",
                    }}
                  >
                    {singleCover ? (
                      pages[0] && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}
                        >
                          <img
                            src={pages[0]}
                            alt="cover"
                            style={{
                              width: "auto",
                              maxWidth: "520px",
                              maxHeight: "76vh",
                              margin: "0 auto",
                              display: "block",
                            }}
                          />
                        </div>
                      )
                    ) : rightSrc ? (
                      <img
                        src={rightSrc}
                        alt="right page"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    ) : (
                      <img
                        src={pages[spreadStart]}
                        alt="single page"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    )}

                    {/* flipping overlay (appears during animation) */}
                    {flipActive && flipImage && (
                      <div
                        className="flip-overlay"
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          transformStyle: "preserve-3d",
                          transformOrigin:
                            animDir === "next" ? "left center" : "right center",
                          transition: `transform ${ANIM_DURATION}ms cubic-bezier(.16,.84,.2,1)`,
                          transform: flipRotate
                            ? animDir === "next"
                              ? "rotateY(-180deg)"
                              : "rotateY(180deg)"
                            : "rotateY(0deg)",
                          boxShadow: "inset 0 0 100px rgba(0,0,0,0.28)",
                          background: "#fff",
                          zIndex: 30,
                          borderRadius: isCover ? 8 : 6,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <img
                            src={flipImage}
                            alt="flip"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              display: "block",
                              backfaceVisibility: "hidden",
                            }}
                          />
                          {/* curl highlight */}
                          <div
                            style={{
                              position: "absolute",
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: 40,
                              pointerEvents: "none",
                              background:
                                "linear-gradient(90deg, rgba(255,255,255,0.0), rgba(255,255,255,0.25))",
                              mixBlendMode: "overlay",
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* spinner overlay while pages are being lazily rendered */}
                  {renderingCount > 0 && (
                    <div
                      aria-hidden={renderingCount === 0}
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: "rgba(255,255,255,0.6)",
                        zIndex: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                        borderRadius: isCover ? 8 : 6,
                      }}
                    >
                      <svg
                        width="48"
                        height="48"
                        viewBox="0 0 40 40"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <g>
                          <circle
                            cx="20"
                            cy="20"
                            r="16"
                            stroke="#eee"
                            strokeWidth="6"
                            fill="none"
                          />
                          <path
                            d="M36 20a16 16 0 0 0-16-16"
                            stroke="#4caf50"
                            strokeWidth="6"
                            strokeLinecap="round"
                            fill="none"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="rotate"
                              from="0 20 20"
                              to="360 20 20"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                          </path>
                        </g>
                      </svg>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            <div
              style={{
                height: 500,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Loading book…
            </div>
          )}
        </div>

        {/* Right arrow */}
        <button
          onClick={onNext}
          aria-label="Next"
          className="btn btn-main"
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 20,
          }}
        >
          ▶
        </button>
      </div>

      {/* Progress bar */}
      <div style={{ maxWidth: "1000px", margin: "20px auto" }}>
        <div
          style={{
            height: 8,
            background: "#eee",
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: "100%",
              background: "#4caf50",
            }}
          />
        </div>
        <div style={{ textAlign: "right", marginTop: 8 }}>{progress}%</div>
      </div>
    </div>
  );
};

export default ReadBook;
